"use client";

import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface Step {
  number: number;
  label: string;
}

interface StepperProps {
  steps: Step[];
  currentStep: number;
  className?: string;
}

const stepConfig = {
  1: { container: "w-[32px]", label: "" },
  2: { container: "w-[52px]", label: "" },
  3: { container: "w-[56px]", label: "" },
} as const;

export function Stepper({ steps, currentStep, className }: StepperProps) {
  return (
    <div className={cn("", className)}>
      <div className="flex justify-center">
        <div className="relative w-full">

          {/* Background line */}
          <div
            className="absolute top-[16px] h-[0.8px] bg-stroke-light z-0"
            style={{ left: "16px", right: "16px" }}
          />

          {/* Steps */}
          <div className="relative flex justify-between items-start">
            {steps.map((step) => {
              const isCompleted = currentStep > step.number;
              const isActive = currentStep === step.number;
              const isInactive = !isCompleted && !isActive;

              return (
                <div key={step.number} className={cn("flex flex-col items-center gap-3 h-[69px]", stepConfig[step.number as keyof typeof stepConfig].container)}>
                  {/* Circle */}
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full p-2 flex items-center justify-center flex-shrink-0 transition-all duration-300 z-10",
                      isCompleted || isActive
                        ? "bg-neutral-500 border-0"
                        : "bg-white border-[0.8px] border-stroke-light"
                    )}
                  >
                    {isCompleted ? (
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    ) : (
                      <span className={cn(
                        "font-oswald font-medium text-[12.8px] leading-[100%] tracking-[0.16px] uppercase",
                        isActive ? "text-white" : "text-neutral-500"
                      )}>
                        {step.number.toString().padStart(2, "0")}
                      </span>
                    )}
                  </div>

                  {/* Label */}
                  <span
                    className={cn(
                      "font-roboto font-medium text-[14px] leading-[25px] tracking-[0%] text-center whitespace-nowrap transition-all duration-300",
                      isCompleted || isActive
                        ? "text-neutral-500"
                        : "text-neutral-300"
                    )}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </div>
  );
}